import React, { useState, useEffect } from 'react';
import Modal from '../../general/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faX, faVolumeUp, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import useAI from '../../../hooks/useAI';
import Button from '../../general/Button';

const AudioRegenerationModal = ({ closeFunction, deckId }) => {
  const [step, setStep] = useState('checking'); // 'checking', 'results', 'processing', 'complete'
  const [checking, setChecking] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardsNeedingAudio, setCardsNeedingAudio] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [totalLanguagesNeedingAudio, setTotalLanguagesNeedingAudio] = useState(0);
  const [processedCards, setProcessedCards] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const { checkMultilingualDeckAudioNeeds, regenerateMultilingualDeckAudioForCards } = useAI();

  useEffect(() => {
    checkAudioNeeds();
  }, []);

  const checkAudioNeeds = async () => {
    if (!deckId) {
      setError('Deck ID is required');
      return;
    }

    setChecking(true);
    setError(null);
    try {
      const result = await checkMultilingualDeckAudioNeeds({ deckId });
      setCardsNeedingAudio(result.cardsNeedingAudio || []);
      setTotalCards(result.totalCards || 0);
      setTotalLanguagesNeedingAudio(result.totalLanguagesNeedingAudio || 0);
      setStep('results');
    } catch (err) {
      console.error('Error checking audio needs:', err);
      setError(err.message || 'Failed to check audio needs');
    } finally {
      setChecking(false);
    }
  };

  const startRegeneration = async () => {
    if (cardsNeedingAudio.length === 0) {
      return;
    }

    setStep('processing');
    setProcessing(true);
    setProcessedCards(0);
    setError(null);

    try {
      const cardDocIds = cardsNeedingAudio.map(card => card.cardDocId);
      
      // Process all cards at once (backend handles it efficiently)
      const finalResult = await regenerateMultilingualDeckAudioForCards({
        deckId: deckId,
        cardDocIds: cardDocIds
      });
      
      // Update progress as we process
      setProcessedCards(finalResult.processed || cardDocIds.length);

      setResult(finalResult);
      setStep('complete');
    } catch (err) {
      console.error('Error regenerating audio:', err);
      setError(err.message || 'Failed to regenerate audio');
      setStep('results');
    } finally {
      setProcessing(false);
    }
  };

  const ModalContent = () => {
    return (
      <div className="flex flex-col gap-4 min-w-[500px] max-w-2xl">
        <h2 className="text-2xl font-bold dark:text-white">Audio Regeneration Check</h2>

        {step === 'checking' && (
          <div className="flex flex-col items-center gap-4 py-8">
            <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-blue-500" />
            <p className="text-gray-600 dark:text-gray-400">Checking which cards need audio regeneration...</p>
          </div>
        )}

        {step === 'results' && (
          <div className="flex flex-col gap-4">
            {error ? (
              <div className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faX} />
                  <span>{error}</span>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Check Results</h3>
                      <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                        <p>Total cards in deck: <strong>{totalCards}</strong></p>
                        <p>Cards needing audio: <strong>{cardsNeedingAudio.length}</strong></p>
                        <p>Total languages needing audio: <strong>{totalLanguagesNeedingAudio}</strong></p>
                      </div>
                    </div>
                  </div>
                </div>

                {cardsNeedingAudio.length === 0 ? (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-300">
                      <FontAwesomeIcon icon={faCheck} />
                      <span className="font-semibold">All audio is up to date!</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="max-h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                      <div className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        Cards needing audio ({cardsNeedingAudio.length}):
                      </div>
                      <div className="space-y-2">
                        {cardsNeedingAudio.map((card, index) => (
                          <div key={card.cardDocId} className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-white dark:bg-gray-700 rounded">
                            Card #{index + 1}: {card.totalLanguages} language{card.totalLanguages !== 1 ? 's' : ''} need{card.totalLanguages === 1 ? 's' : ''} audio
                            {card.languages && card.languages.length > 0 && (
                              <div className="ml-4 mt-1 text-gray-500 dark:text-gray-500">
                                Languages: {card.languages.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3 justify-end">
                      <Button text="Cancel" color="red" onClick={closeFunction} />
                      <Button 
                        text="Regenerate Audio" 
                        color="green" 
                        onClick={startRegeneration}
                        disabled={processing}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {step === 'processing' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-blue-500" />
              <div className="flex-1">
                <div className="text-lg font-semibold dark:text-white">Regenerating Audio...</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Processing {cardsNeedingAudio.length} card{cardsNeedingAudio.length !== 1 ? 's' : ''} ({totalLanguagesNeedingAudio} language{totalLanguagesNeedingAudio !== 1 ? 's' : ''})...
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This may take a few minutes. Please wait...
                </div>
              </div>
            </div>
            
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
              <div 
                className="bg-blue-500 h-4 transition-all duration-300 ease-out animate-pulse"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        {step === 'complete' && result && (
          <div className="flex flex-col gap-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-2">
                <FontAwesomeIcon icon={faCheck} />
                <span className="font-semibold">Audio Regeneration Complete!</span>
              </div>
              <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
                <p>Processed: <strong>{result.processed}</strong> out of <strong>{result.total}</strong> cards</p>
                <p>Time taken: <strong>{result.timeSeconds}</strong> seconds</p>
                {result.errors && result.errors.length > 0 && (
                  <p className="text-red-600 dark:text-red-400">
                    Errors: <strong>{result.errors.length}</strong> card(s) had errors
                  </p>
                )}
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="max-h-32 overflow-y-auto border border-red-200 dark:border-red-800 rounded-lg p-3 bg-red-50 dark:bg-red-900/20">
                <div className="text-sm font-semibold mb-2 text-red-700 dark:text-red-300">
                  Errors ({result.errors.length}):
                </div>
                <div className="space-y-1 text-xs text-red-600 dark:text-red-400">
                  {result.errors.map((err, index) => (
                    <div key={index}>
                      Card {err.cardId}: {err.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button text="Close" color="green" onClick={closeFunction} />
            </div>
          </div>
        )}

        {(step === 'results' && cardsNeedingAudio.length === 0) && (
          <div className="flex justify-end">
            <Button text="Close" color="green" onClick={closeFunction} />
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      content={<ModalContent />}
      closeFunction={closeFunction}
      size="2xl"
    />
  );
};

export default AudioRegenerationModal;
